/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import Keycloak from '@inoventoryapps/keycloak-js';

@Injectable({
    providedIn: 'root',
})
export class KeyService {
    private db!: IDBDatabase;
    private dbPromise?: Promise<IDBDatabase>;
    private keycloak = inject(Keycloak);
    keyClockJwk: any;
    fdmJwk: any;
    serverKeyRes: any;
    keyClockKeyRes: any;

    /**
     * Opens the crypto-keys IndexedDB and generates this session's key pairs.
     *
     * Idempotent — the promise is cached, so the app initializer and any caller that
     * needs a key share a single database open and a single key generation. Opening
     * twice used to race two `generateKeys()` runs, the second overwriting the pair
     * the first had stored.
     * @returns the open database
     */
    initDB(): Promise<IDBDatabase> {
        this.dbPromise ??= new Promise((resolve, reject) => {
            const request = indexedDB.open('crypto-keys', 2);
            request.onupgradeneeded = (event: any) => {
                this.db = event.target.result;
                this.db.createObjectStore('keys', { keyPath: 'name' });
            };
            request.onsuccess = (event: any) => {
                this.db = event.target.result;
                // Resolve only once the key pairs are committed, so a caller can never
                // read a record that has not been written yet.
                this.generateKeys().then(() => resolve(this.db), reject);
            };
            request.onerror = (event: any) => {
                reject(event.target.error);
            };
        });

        return this.dbPromise;
    }

    /**
     * Insert key in indexed db
     */
    async generateKeys() {
        const keyPair = await window.crypto.subtle.generateKey(
            {
                name: 'ECDH',
                namedCurve: 'P-256',
            },
            false,
            ['deriveKey', 'deriveBits'],
        );

        const signKeyPair = await window.crypto.subtle.generateKey(
            {
                name: 'ECDSA',
                namedCurve: 'P-256',
            },
            false,
            ['verify', 'sign'],
        );

        await new Promise<void>((resolve, reject) => {
            const transaction = this.db.transaction(['keys'], 'readwrite');
            const store = transaction.objectStore('keys');
            store.put({ name: 'client-key', key: keyPair });
            store.put({ name: 'sign-key', key: signKeyPair });
            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
            transaction.onabort = () => reject(transaction.error);
        });
    }

    /**
     *  Get Client Keys
     * @returns client key (UI)
     */
    async getClientKey() {
        return this.getStoredKey('client-key');
    }

    /**
     * Get Sign Key
     * @returns Sign Key
     */
    async getSignKey() {
        return this.getStoredKey('sign-key');
    }

    /**
     * Read one key pair from the crypto-keys store, opening the database first if a
     * caller reaches this before the app initializer has run.
     * @param name record name
     * @returns the stored key pair
     */
    private async getStoredKey(name: string): Promise<unknown> {
        const db = await this.initDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['keys'], 'readonly');
            const store = transaction.objectStore('keys');
            const request = store.get(name);
            request.onsuccess = () => {
                if (!request.result) {
                    reject(new Error(`Key "${name}" is missing from the crypto-keys store`));
                    return;
                }
                resolve(request.result.key);
            };
            request.onerror = () => reject(request.error);
        });
    }

    getKid(url: any) {
        if (url.includes('api/keycloak/v2/graph/')) {
            return this.keyClockJwk.keys[0].kid;
        } else {
            return this.fdmJwk.keys[0].kid;
        }
    }

    /**
     * Get Server Public Key
     * @param jwksUrl
     * @returns key from server
     */
    async getServerPublicKey(jwksUrl: string, url: any): Promise<CryptoKey> {
        if (url.includes('api/keycloak/v2/graph/')) {
            if (!this.keyClockKeyRes) {
                const response = await fetch(jwksUrl);
                const jwks = await response.json();
                this.keyClockJwk = jwks;
                const serverKey = jwks.keys[0];
                this.keyClockKeyRes = serverKey;
            }
        } else {
            if (!this.serverKeyRes) {
                const response = await fetch(jwksUrl);
                const jwks = await response.json();
                this.fdmJwk = jwks;
                const serverKey = jwks.keys[0];
                this.serverKeyRes = serverKey;
            }
        }

        return crypto.subtle.importKey(
            'jwk',
            url.includes('api/keycloak/v2/graph/') ? this.keyClockKeyRes : this.serverKeyRes,
            {
                name: 'ECDH',
                namedCurve: 'P-256',
            },
            true,
            [],
        );
    }

    async signJwt(header: any, method: string, url: string) {
        const keyPair: any = await this.getSignKey();
        const clientKeyPair: any = await this.getClientKey();
        const privateKey = keyPair.privateKey;

        const JWKDPOPPublicKey = await window.crypto.subtle.exportKey('jwk', keyPair.publicKey);
        const JWKClientPublicKey = await window.crypto.subtle.exportKey('jwk', clientKeyPair.publicKey);

        const dpopHeader = {
            typ: 'dpop+jwt',
            alg: 'ECDSA',
            jwk: JWKDPOPPublicKey,
        };

        const payload = {
            jti: crypto.randomUUID(),
            htm: method,
            htu: url,
            iat: Math.floor(Date.now() / 1000),
            ath: await this.hashToken(this.keycloak.token || ''),
            enc: {
                jwk: JWKClientPublicKey,
                kid: this.serverKeyRes.kid,
                iv: this.arrayBufferToBase64(header.iv),
            },
        };

        const encoder = new TextEncoder();
        const encodedHeader = this.arrayBufferToBase64(encoder.encode(JSON.stringify(dpopHeader)));
        const encodedPayload = this.arrayBufferToBase64(encoder.encode(JSON.stringify(payload)));

        const data = encoder.encode(`${encodedHeader}.${encodedPayload}`);
        const signature = await crypto.subtle.sign(
            {
                name: 'ECDSA',
                hash: { name: 'SHA-256' },
            },
            privateKey,
            data,
        );

        const encodedSignature = this.arrayBufferToBase64(new Uint8Array(signature));
        return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
    }

    /**
     * Hash Token using SHA-256
     * @param jwtToken
     * @returns
     */
    async hashToken(jwtToken: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(jwtToken);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
        return hashHex;
    }

    /**
     * Convert ArrayBuffer to Base64
     * @param buffer
     * @returns Base64
     */
    arrayBufferToBase64(buffer: any) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    /**
     * Convert Base64 to ArrayBuffer
     * @param base64
     * @returns ArrayBuffer
     */
    base64ToArrayBuffer(base64: any) {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
}
